import { useServiceHistories } from '@hooks';
import { ServiceHistory } from '@services';
import { useStore } from '@stores';
import { observer } from 'mobx-react';
import React, { useCallback, useState } from 'react';
import Presentation from './VehicleHistoryTable.presentation';

interface IProps {
  vin: string;
  customerId?: string;
  customerEmail?: string;
  customerFullName?: string;
  compact?: boolean;
}

export const VehicleHistoryTable: React.FC<IProps> = observer(({ vin, customerId, customerEmail, customerFullName, compact = false }) => {
  const { serviceHistoryStore } = useStore();

  const [open, setOpen] = useState(false);
  const [serviceHistory, setServiceHistory] = useState<ServiceHistory | null>(null);

  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailServiceHistoryId, setEmailServiceHistoryId] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSending, setEmailSending] = useState(false);

  const historyHook = useServiceHistories(vin);

  const handleOpen = (serviceHistory: ServiceHistory) => {
    setOpen(true);
    setServiceHistory(serviceHistory);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const validateEmail = (val: string) => {
    if (!val) return null;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(val)) return 'Invalid email address';
    return null;
  };

  const handleEmailOpen = useCallback((serviceHistory: ServiceHistory) => {
    setEmailServiceHistoryId(serviceHistory.id);
    setEmail(customerEmail || '');
    setEmailError(null);
    setEmailModalOpen(true);
  }, [customerEmail]);

  const handleEmailCancel = useCallback(() => {
    setEmailModalOpen(false);
    setEmailServiceHistoryId(null);
    setEmail('');
    setEmailError(null);
  }, []);

  const handleEmailChange = (val: string) => {
    setEmail(val);
    setEmailError(validateEmail(val));
  };

  const handleEmailSend = useCallback(async () => {
    if (!emailServiceHistoryId) return;

    const error = validateEmail(email);
    setEmailError(error);
    if (error) return;

    setEmailSending(true);
    try {
      await serviceHistoryStore.sendServiceHistoryEmail(
        emailServiceHistoryId,
        email || customerEmail,
        customerFullName
      );
      setEmailModalOpen(false);
    } catch {
      // error handled by store
    } finally {
      setEmailSending(false);
    }
  }, [serviceHistoryStore, emailServiceHistoryId, email, customerEmail, customerFullName]);

  return (
    <Presentation
      open={open}
      customerId={customerId}
      historyHook={historyHook}
      serviceHistory={serviceHistory}
      setOpen={setOpen}
      handleOpen={handleOpen}
      handleClose={handleClose}
      emailModalOpen={emailModalOpen}
      email={email}
      emailError={emailError}
      emailSending={emailSending}
      handleEmailOpen={handleEmailOpen}
      handleEmailCancel={handleEmailCancel}
      handleEmailChange={handleEmailChange}
      handleEmailSend={handleEmailSend}
      compact={compact}
    />
  );
});
